import { useMemo, useState } from 'react';

import type { S3Object } from '../../../drive/types';
import { getLastPathSegment } from '../helpers';
import type { SortableField, SortDirection, SortOptions } from '../types/sorting';

// Derive a human-friendly name for sorting by Key.
// - Parent directory is always shown as "../"
// - If currentPath is a prefix, strip it to sort by the displayed leaf name
// - For directories, keep the trailing slash to differentiate visually
function getDisplayNameForSorting(item: S3Object, currentPath?: string): string {
  const isParentDirectory = item.isParentDirectory;
  const key = item.Key || 'Unknown';

  if (isParentDirectory) return '../';

  if (currentPath && key.startsWith(currentPath)) {
    const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
    return key.substring(normalizedCurrentPath.length);
  }

  const lastName = getLastPathSegment(key);
  return key.endsWith('/') ? `${lastName}/` : lastName;
}

export const useSorting = (objects: S3Object[] | undefined, options: SortOptions = {}) => {
  const { currentPath } = options;

  const [sortField, setSortField] = useState<SortableField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sortedObjects = useMemo(() => {
    if (!objects || !sortField) return objects || [];

    // Compute a normalized "type" for display and sorting.
    // Directories sort as 'directory'; files sort by lowercase extension or 'unknown'.
    const getType = (item: S3Object): string => {
      const isDir = item.Key?.endsWith('/') || item.isParentDirectory;
      if (isDir) return 'directory';
      const key = item.Key || '';
      const lastSlash = key.lastIndexOf('/');
      const lastDot = key.lastIndexOf('.');
      const ext = lastDot > lastSlash ? key.substring(lastDot + 1).toLowerCase() : '';
      return ext || 'unknown';
    };

    // For each sortable field, provide a selector that extracts a comparable value.
    // This keeps the sorting logic concise and easy to extend.
    const selectors: Record<Exclude<SortableField, null>, (o: S3Object) => string | number> = {
      Key: (o) => getDisplayNameForSorting(o, currentPath).toLowerCase(),
      Size: (o) => o.Size ?? 0,
      LastModified: (o) => (o.LastModified ? new Date(o.LastModified).getTime() : 0),
      Type: (o) => getType(o),
    };

    const multiplier = sortDirection === 'asc' ? 1 : -1;

    return [...objects].sort((a, b) => {
      // Parent directory "../" always comes first, above everything else
      const aIsParentDir = a.isParentDirectory;
      const bIsParentDir = b.isParentDirectory;

      if (aIsParentDir && !bIsParentDir) return -1;
      if (!aIsParentDir && bIsParentDir) return 1;
      if (aIsParentDir && bIsParentDir) return 0; // Both are parent dirs, keep original order

      // Then group regular directories before files
      const aIsDir = (a.Key && a.Key.endsWith('/')) || a.isParentDirectory;
      const bIsDir = (b.Key && b.Key.endsWith('/')) || b.isParentDirectory;
      if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;

      // Compare by the selected field using the normalized values from selectors.
      const selector = selectors[sortField];
      const aVal = selector(a);
      const bVal = selector(b);

      // Numeric fields are compared arithmetically; strings use localeCompare.
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }

      const result = String(aVal).localeCompare(String(bVal));
      return result * multiplier;
    });
  }, [objects, sortField, sortDirection, currentPath]);

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return {
    sortedObjects,
    sortField,
    sortDirection,
    handleSort,
  };
};
